import React, { useState } from 'react';
import { View, Pressable, ScrollView, useWindowDimensions, Platform } from 'react-native';
import type { BlurViewProps } from 'expo-blur';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ChevronUpIcon, ChevronDownIcon } from '@/components/ui/image-viewer/ChevronIcons';
import { RenderHtml } from '@/components/ui/RenderHtml';
import { Photo } from '@/models/Photo';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { Text } from '@/components/gluestack/text';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { useTranslation } from 'react-i18next';
import { config as gluestackConfig } from '@/components/gluestack/gluestack-ui-provider/config';
import { VStack } from '~/components/gluestack/vstack';
import { HugeIconsWrapper } from '../HugeIconsWrapper';
import { UniversalAccessCircleIcon } from '@hugeicons-pro/core-stroke-rounded';
import { HStack } from '~/components/gluestack/hstack';
import { Box } from '~/components/gluestack/box';
import { LIKES_OVERLAY_CAPTION_RESERVED_SPACE } from '@/components/ui/image-viewer/ImageViewerConstants';
import { SafeBlurView } from '@/components/ui/SafeBlurView';

interface IImageViewerBottomSheetProps {
  photo?: Photo | null;
  uiVisible: boolean;
  isClosing: boolean;
  blurTarget?: BlurViewProps['blurTarget'];
}

export const ImageViewerBottomSheet: React.FC<IImageViewerBottomSheetProps> = ({
  photo,
  uiVisible,
  isClosing,
  blurTarget,
}) => {
  const screen = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isLargeScreen = useIsLargeScreen();
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [captionIsTruncated, setCaptionIsTruncated] = useState(false);
  const { t } = useTranslation();
  // The floating likes/comments overlay only renders on small screens.
  // On large screens we skip the bottom clearance entirely so the caption
  // can use its full natural padding.
  const overlayReservedSpace = isLargeScreen ? 0 : LIKES_OVERLAY_CAPTION_RESERVED_SPACE;

  // Staff (web) get the rating badge even on captionless photos
  const showsRating = photo?.IsOwner || (Platform.OS === 'web' && photo?.CanModerate);
  if (!photo || !uiVisible || (!photo.HasCaption && !showsRating)) return null;

  const maxCaptionHeight = screen.height / 2;
  const toggleCaptionExpansion = () => {
    setCaptionExpanded(!captionExpanded);
  };

  const handleTruncationChange = (isTruncated: boolean) => {
    setCaptionIsTruncated(isTruncated);
  };

  return (
    <View
      pointerEvents="box-none"
      className={`absolute left-0 right-0 z-20 overflow-hidden ${captionIsTruncated || captionExpanded ? 'rounded-t-xl' : ''}`}
      style={[{ opacity: isClosing ? 0 : 1, maxHeight: maxCaptionHeight, bottom: 0 }]}>
      <SafeBlurView
        blurTarget={blurTarget}
        intensity={80}
        tint="dark"
        className="px-4 pt-4"
        // Bottom padding lives on the dark-blur card itself (rather than inside
        // the ScrollView's contentContainerStyle). Doing it here is reliable on
        // every platform — including react-native-web, where ScrollView's inner
        // paddingBottom doesn't always grow the surrounding absolutely-
        // positioned wrapper, leaving the caption text flush against the
        // overlay. Putting the reserved space directly on SafeBlurView grows
        // the dark card itself, so its visual bottom extends behind the
        // overlay regardless of caption length.
        //
        // The 16 base = original p-4 bottom padding. overlayReservedSpace is a
        // fixed 60 on mobile / small web (= 16 anchor offset + 44 overlay
        // height) and 0 on desktop. See ImageViewerConstants for details.
        style={{
          maxHeight: maxCaptionHeight,
          paddingBottom: 16 + insets.bottom + overlayReservedSpace,
        }}>
        <Pressable
          onPress={toggleCaptionExpansion}
          className="-mt-2 mb-2 flex-row justify-center"
          hitSlop={{ top: 10, bottom: 10, left: 20, right: 20 }}>
          {captionExpanded ? (
            <ChevronDownIcon size={24} color="white" />
          ) : captionIsTruncated ? (
            <ChevronUpIcon size={24} color="white" />
          ) : null}
        </Pressable>
        <ScrollView
          scrollEnabled={captionExpanded}
          showsVerticalScrollIndicator={true}
          // Account for chevron + SafeBlurView top/bottom padding so an
          // expanded long caption never makes the dark card exceed
          // maxCaptionHeight.
          style={{ maxHeight: maxCaptionHeight - 56 - insets.bottom - overlayReservedSpace }}>
          <VStack className="gap-1">
            {photo.HasCaption ? (
              <RenderHtml
                html={photo.Caption}
                size="md"
                className="leading-tight text-white"
                detectHttpsLinks={true}
                detectHashtags
                noReadMoreButton={true}
                noTruncate={captionExpanded}
                characterLimit={isLargeScreen ? 250 : 125}
                onTruncationChange={handleTruncationChange}
              />
            ) : null}

            {showsRating ? (
              <HStack className={`my-2 items-center gap-2`} style={gluestackConfig.dark}>
                <HugeIconsWrapper
                  icon={UniversalAccessCircleIcon}
                  size={22}
                  className="text-white/80"
                />
                <Badge size="md" action="muted" className="bg-white/20">
                  <BadgeText>
                    {t(`PhotoRating.${photo.Rating ?? 'other'}.Text`, { ns: 'domain' })}
                  </BadgeText>
                </Badge>
                {photo.Review === 'queued' || photo.Review === 'reviewed' ? (
                  <Badge
                    size="md"
                    action={photo.Review === 'queued' ? 'warning' : 'success'}>
                    <BadgeText>
                      {t(`PhotoReview.${photo.Review}.Text`, { ns: 'domain' })}
                    </BadgeText>
                  </Badge>
                ) : null}
                <Box className="flex-1">
                  <Text size="sm" className="text-white/80">
                    {(() => {
                      const description = t(`PhotoRating.${photo.Rating ?? 'other'}.Description`, {
                        ns: 'domain',
                      });
                      return description ? `${description}` : '';
                    })()}
                  </Text>
                </Box>
              </HStack>
            ) : null}
          </VStack>
        </ScrollView>
      </SafeBlurView>
    </View>
  );
};
